/**
 * 시나리오 적용 기록의 화면 로그 채널 — 어댑터가 동작을 적용·복원·실패할 때 `sim.screenlog.v1` 에 이 채널로 한 줄을 낸다
 * (adapter-core `ScenarioRunner(on_line=…)` → `open_screen_log(channel="scenario")`). 줄의 본문은 글이 아니라 **JSON 객체
 * 하나**(adapter-core `event_of`, 계약 `docs/sim-stream-contract-design.md` §6)다 — 웹이 글을 짓고 차트에 표식을 둔다.
 * 웹은 참가자 종류의 채널 필터와 무관하게 이 채널을 받아 그 참가자의 로그 창에 싣는다. 이름은 계약 §6 과 같은 글자.
 */
import { SCENARIO_OPS, type ScenarioOp } from './schema';

export const SCENARIO_LOG_CHANNEL = 'scenario';

export const SCENARIO_OUTCOMES = ['applied', 'failed', 'overridden', 'skipped'] as const;
export type ScenarioOutcome = (typeof SCENARIO_OUTCOMES)[number];

/** 어댑터가 낸 사건 하나 — 계약 §6 의 키 그대로. `t` 는 그 일이 일어난 스텝 경계의 sim 초(없으면 경계 밖의 사건) */
export interface ScenarioLogEvent {
  kind: 'outcome' | 'ended';
  index: number;
  input: string;
  op: ScenarioOp;
  value: number | boolean;
  at: number | null;
  when: { variable: string; cmp: string; threshold: number; after: number } | null;
  duration: number | null;
  width: number | null;
  t: number | null;
  outcome: ScenarioOutcome | null;
  detail: string;
  how: 'ramp done' | 'released' | null;
}

/** 계약 §6 의 키 — 사건 줄은 이 키를 모두 싣는다(adapter-core `event_of`) */
export const SCENARIO_LOG_KEYS = ['kind', 'index', 'input', 'op', 'value', 'at', 'when', 'duration', 'width', 't', 'outcome', 'detail', 'how'] as const;

const num = (v: unknown): number | null => (typeof v === 'number' && Number.isFinite(v) ? v : null);

/** 채널의 줄 → 사건. 계약에 맞지 않는 줄(옛 어댑터의 글, 손상)은 undefined — 호출자가 원문을 그대로 보인다 */
export function parseScenarioLogEvent(line: string): ScenarioLogEvent | undefined {
  let raw: unknown;
  try {
    raw = JSON.parse(line);
  } catch {
    return undefined;
  }
  if (!raw || typeof raw !== 'object') return undefined;
  const r = raw as Record<string, unknown>;
  // 계약 §6 «키가 모자란 줄은 원문 그대로» — 값이 null 일 수 있는 키도 있어야 한다
  if (!SCENARIO_LOG_KEYS.every((k) => k in r)) return undefined;
  if ((r.kind !== 'outcome' && r.kind !== 'ended') || typeof r.index !== 'number' || typeof r.input !== 'string') return undefined;
  if (!(SCENARIO_OPS as readonly string[]).includes(String(r.op))) return undefined;
  if (typeof r.value !== 'number' && typeof r.value !== 'boolean') return undefined;
  const w = r.when && typeof r.when === 'object' ? (r.when as Record<string, unknown>) : null;
  const outcome = (SCENARIO_OUTCOMES as readonly string[]).includes(String(r.outcome)) ? (r.outcome as ScenarioOutcome) : null;
  const how = r.how === 'ramp done' || r.how === 'released' ? r.how : null;
  return {
    kind: r.kind,
    index: r.index,
    input: r.input,
    op: r.op as ScenarioOp,
    value: r.value,
    at: num(r.at),
    when: w && typeof w.variable === 'string' ? { variable: w.variable, cmp: String(w.cmp ?? ''), threshold: num(w.threshold) ?? 0, after: num(w.after) ?? 0 } : null,
    duration: num(r.duration),
    width: num(r.width),
    t: num(r.t),
    outcome,
    detail: typeof r.detail === 'string' ? r.detail : '',
    how,
  };
}

const OUTCOME_WORDS: Record<ScenarioOutcome, string> = {
  applied: '적용',
  failed: '실패',
  overridden: '취소(다른 동작·외부 쓰기에 밀림)',
  skipped: '건너뜀',
};

const sec = (v: number) => `${Number.isInteger(v) ? v : Number(v.toFixed(3))} s`;

/** 동작의 글 — «20 s: set 'node_hfctl001.q' = 50000» / «조건 'y' > 561: set … = 0» (시나리오 탭의 칸 순서와 같은 말) */
export function describeScenarioLogAction(ev: ScenarioLogEvent): string {
  const trigger = ev.at !== null ? sec(ev.at) : ev.when ? `조건 ${ev.when.variable} ${ev.when.cmp} ${ev.when.threshold}${ev.when.after ? ` (${sec(ev.when.after)} 이후)` : ''}` : '';
  const extra = ev.op === 'ramp' && ev.duration !== null ? ` (${sec(ev.duration)} 동안)` : ev.op === 'pulse' && ev.width !== null ? ` (${sec(ev.width)} 폭)` : '';
  return `${trigger}: ${ev.op} ${ev.input} = ${String(ev.value)}${extra}`;
}

/** 로그 창에 싣는 한 줄 — 어댑터 글 대신 웹이 짓는다(시나리오 탭·시작 창과 같은 낱말) */
export function scenarioLogEventText(ev: ScenarioLogEvent): string {
  const head = `시나리오 #${ev.index} ${describeScenarioLogAction(ev)}`;
  if (ev.kind === 'ended') {
    const how = ev.how === 'released' ? '발동 전 값으로 되돌림' : 'ramp 끝';
    return `${head} — ${how}${ev.t !== null ? ` (${sec(ev.t)})` : ''}`;
  }
  const word = ev.outcome ? OUTCOME_WORDS[ev.outcome] : '';
  const when = ev.t !== null ? ` (${sec(ev.t)})` : '';
  return `${head} — ${word}${when}${ev.detail ? `: ${ev.detail}` : ''}`;
}

/** 차트 세로 표식에 쓰는 사건 — sim 시각이 있고 실제로 값이 들어가거나 거둔 것(실패·취소·건너뜀은 표식이 아니라 로그) */
export function scenarioMarkEvents(events: readonly ScenarioLogEvent[]): ScenarioLogEvent[] {
  return events.filter((ev) => ev.t !== null && (ev.kind === 'ended' || ev.outcome === 'applied'));
}

/** 표식의 짧은 이름 — «#0 set» / «#1 ramp 끝» */
export function scenarioMarkLabel(ev: ScenarioLogEvent): string {
  if (ev.kind === 'ended') return `#${ev.index} ${ev.how === 'released' ? '되돌림' : 'ramp 끝'}`;
  return `#${ev.index} ${ev.op}`;
}
